import { Injectable } from '@angular/core';

import { AddressModel } from 'src/models/address.model';
import {
  SteadilyCoverageDetails,
  SteadilyEstimate,
  SteadilyProductCode,
  SteadilyPropertyType,
} from 'src/models/steadily.model';

/**
 * Stand-in for `POST /v1/quote/estimate?include_coverages=true`.
 *
 * Returns the real Steadily response shape, but prices are derived locally from the
 * property's own square footage and age rather than fetched. Swap `buildEstimateForAddress`
 * for the HTTP call once a partner API key exists — nothing downstream needs to change.
 *
 * Pricing is deterministic: the same property always yields the same estimate.
 */

/** Replacement cost per square foot, before the age adjustment. */
const REPLACEMENT_COST_PER_SQFT = 165;

/** Annual DP3 premium as a fraction of replacement value. */
const DP3_RATE = 0.0042;

/** DP1 (actual cash value) runs above DP3 on Steadily's book more often than not. */
const DP1_MULTIPLIER = 1.17;

const DEFAULT_SQFT = 1600;
const DEFAULT_YEAR_BUILT = 1995;

const PROPERTY_TYPE_MAP: Record<string, SteadilyPropertyType> = {
  single_family: 'SINGLE_FAMILY',
  duplex: 'DUPLEX',
  triplex: 'TRIPLEX',
  quadplex: 'QUADPLEX',
  multi_family: 'APARTMENT_BUILDING',
  apartment: 'APARTMENT_BUILDING',
  mobile_home: 'MOBILE_HOME',
};

@Injectable({
  providedIn: 'root',
})
export class SteadilyMockService {

  private useDummyData = true;

  buildEstimateForAddress(address: AddressModel): SteadilyEstimate {
    const sqft = address.living_area_sqft || DEFAULT_SQFT;
    const yearBuilt = address.year_built || DEFAULT_YEAR_BUILT;
    const replacementValue = Math.round((sqft * REPLACEMENT_COST_PER_SQFT) / 1000) * 1000;

    const dp3 = this.roundToDollarsPerMonth(replacementValue * DP3_RATE * this.ageFactor(yearBuilt));
    const dp1 = this.roundToDollarsPerMonth(dp3 * DP1_MULTIPLIER);

    return {
      property_id: `tidy-addr-${address.id}`,
      // Steadily issues a per-property link only with a real estimate; there is none to fake.
      start_url: '',
      savings: { lowest: dp3, savings: 0 },
      estimate: { lowest: Math.min(dp1, dp3), dp_1: dp1, dp_3: dp3 },
      minimum_earned_premium_days: 90,
      product_options: [
        {
          product: 'DP3',
          premium: dp3,
          coverages: this.buildCoverages('DP3', replacementValue),
        },
        {
          product: 'DP1',
          premium: dp1,
          coverages: this.buildCoverages('DP1', replacementValue),
        },
      ],
      calculated_property: {
        property_id: `tidy-addr-${address.id}`,
        address: {
          street_address: address.address1 || '',
          street_address_2: address.address2 || '',
          city: address.city || '',
          state: address.state || '',
          zip_code: address.zip || '',
          lat: address.latitude,
          lng: address.longitude,
        },
        property_details: {
          size_sqft: sqft,
          year_built: yearBuilt,
          number_of_units: 1,
          property_type: PROPERTY_TYPE_MAP[address.property_type] || 'OTHER',
          replacement_value: replacementValue,
          stories: 1,
          bedrooms: address.bed_count || 0,
          bathrooms: address.bath_count || 0,
        },
      },
    };
  }

  /** Older construction rates higher; matches how Steadily's own book is shaped. */
  private ageFactor(yearBuilt: number): number {
    if (yearBuilt < 1960) { return 1.28; }
    if (yearBuilt < 1980) { return 1.18; }
    if (yearBuilt < 2000) { return 1.08; }
    return 1;
  }

  /** Premiums quote as whole dollars, and divide evenly into 12 monthly payments. */
  private roundToDollarsPerMonth(amount: number): number {
    return Math.round(amount / 12) * 12;
  }

  private buildCoverages(product: SteadilyProductCode, replacementValue: number): SteadilyCoverageDetails {
    const isReplacementCost = product === 'DP3';
    return {
      loss_valuation: isReplacementCost ? 'Replacement Cost' : 'Actual Cash Value',
      dwelling_limit: replacementValue,
      private_structures_limit: Math.round(replacementValue * 0.1),
      personal_property_limit: isReplacementCost ? 3000 : 1500,
      loss_of_rents_limit: Math.round(replacementValue * 0.1),
      all_other_perils_deductible: '$1,000',
      wind_hail_deductible: isReplacementCost ? '1%' : '2%',
    };
  }

  get isUsingMockPricing(): boolean {
    return this.useDummyData;
  }
}
